import {
  createContext,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";

type CheckboxRegistry = Map<string, HTMLInputElement>;

interface NMEAPreviewCheckboxNavigationContextValue {
  register: (id: string, element: HTMLInputElement | null) => void;
  handleKeyDown: (id: string, event: KeyboardEvent<HTMLInputElement>) => void;
}

const NMEAPreviewCheckboxNavigationContext =
  createContext<NMEAPreviewCheckboxNavigationContextValue | null>(null);

const parseNavigationId = (id: string) => {
  const dashIndex = id.indexOf("-");
  const section = Number(id.slice(0, dashIndex));
  const suffix = id.slice(dashIndex + 1);
  const row = suffix === "header" ? -1 : Number(suffix);
  return { section, row };
};

const compareNavigationIds = (a: string, b: string) => {
  const aParsed = parseNavigationId(a);
  const bParsed = parseNavigationId(b);
  return aParsed.section - bParsed.section || aParsed.row - bParsed.row;
};

export const NMEAPreviewCheckboxNavigationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const registryRef = useRef<CheckboxRegistry>(new Map());

  const register = useCallback(
    (id: string, element: HTMLInputElement | null) => {
      if (element) {
        registryRef.current.set(id, element);
        return;
      }
      registryRef.current.delete(id);
    },
    [],
  );

  const handleKeyDown = useCallback(
    (id: string, event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;

      const ids = Array.from(registryRef.current.keys()).sort(
        compareNavigationIds,
      );
      const currentIndex = ids.indexOf(id);
      if (currentIndex === -1) return;

      const direction = event.key === "ArrowDown" ? 1 : -1;
      let nextIndex = currentIndex + direction;

      while (nextIndex >= 0 && nextIndex < ids.length) {
        const nextElement = registryRef.current.get(ids[nextIndex]);
        if (nextElement && !nextElement.disabled) {
          event.preventDefault();
          nextElement.focus();
          return;
        }
        nextIndex += direction;
      }
    },
    [],
  );

  const value = useMemo(
    () => ({ register, handleKeyDown }),
    [register, handleKeyDown],
  );

  return (
    <NMEAPreviewCheckboxNavigationContext.Provider value={value}>
      {children}
    </NMEAPreviewCheckboxNavigationContext.Provider>
  );
};

export const useNMEAPreviewCheckboxNavigation = (navigationId?: string) => {
  const context = useContext(NMEAPreviewCheckboxNavigationContext);

  const setCheckboxRef = useCallback(
    (element: HTMLInputElement | null) => {
      if (!navigationId) return;
      context?.register(navigationId, element);
    },
    [context, navigationId],
  );

  const onCheckboxKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (!navigationId) return;
      context?.handleKeyDown(navigationId, event);
    },
    [context, navigationId],
  );

  return { setCheckboxRef, onCheckboxKeyDown };
};
